<template>
  <transition name="fade">
    <div v-if="visible" class="fixed" @click="closePopup">
      <div class="modal-card" @click.stop>
        <div class="modal-header">
          <div class="flex items-center gap-sm">
            <span class="badge badge-warning">Taller Privado</span>
            <code class="code-badge">{{ privatePlanPurchaseData.purchase.codigo }}</code>
          </div>
          <button type="button" class="close-btn" @click="closePopup">&times;</button>
        </div>

        <div class="modal-body">
          <h2 style="font-size: 1.35rem; margin-bottom: 0.5rem; color: var(--text-color-dark);">
            {{ privatePlanPurchaseData.purchase.titulo }}
          </h2>

          <div class="details-box">
            <div class="detail-item" v-if="privatePlanPurchaseData.purchase.observaciones">
              <strong>Observaciones:</strong> {{ privatePlanPurchaseData.purchase.observaciones }}
            </div>
            <div class="detail-row">
              <div class="detail-item">
                <strong>Plazas:</strong> {{ privatePlanPurchaseData.purchase.plazas }}
              </div>
              <div class="detail-item">
                <strong>Fecha:</strong> {{ formatFecha(privatePlanPurchaseData.purchase.fecha) }} a las {{ formatHora(privatePlanPurchaseData.purchase.fecha) }}
              </div>
            </div>
          </div>

          <div class="user-card">
            <span class="user-card-label">Usuario responsable del grupo</span>
            <div class="user-info">
              <img :src="privatePlanPurchaseData.user.foto" alt="Imagen del usuario" class="circular-image" />
              <div class="user-text">
                <h3>{{ privatePlanPurchaseData.user.nombre }}</h3>
                <p>{{ privatePlanPurchaseData.user.email }}</p>
              </div>
            </div>
          </div>

          <div style="margin-top: 1.5rem;">
            <button 
              v-if="privatePlanPurchaseData.purchase.estado == 'Pendiente'" 
              @click="redeemPrivatePlan" 
              class="main_button-green w-full"
              :class="redeemButtonClass" 
              style="padding: 12px;"
            >
              Canjear Taller
            </button>
            <div 
              v-else-if="privatePlanPurchaseData.purchase.estado == 'Canjeado'" 
              class="badge badge-success w-full"
              style="padding: 12px; font-size: 14px;"
            >
              Este taller ya ha sido canjeado
            </div>
            <div 
              v-else 
              class="badge badge-danger w-full"
              style="padding: 12px; font-size: 14px;"
            >
              Este taller ha caducado
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
  
  <script setup>
  import { ref, watch, defineEmits, defineProps } from 'vue';
  import { format, parse } from 'date-fns';
  import { es } from 'date-fns/locale';
  import { useLoadingStore } from "@/stores/loadingStore";
  import { refreshToken } from "@/services/authService";
  import { useRouter } from 'vue-router';

  const props = defineProps({
    privatePlanPurchaseData: Object,
  });
  
  const loadingStore = useLoadingStore();
  const emit = defineEmits(['errorRedeeming', 'successRedeeming']);
  const router = useRouter();

  const visible = ref(false);

  watch(() => props.privatePlanPurchaseData, (newVal) => {
    visible.value = !!newVal;
  });
  
  const closePopup = () => {
    visible.value = false;
    history.back();
  };

  const formatFecha = (fecha) => {
    const fechaAux = parse(fecha, "yyyy-MM-dd HH:mm:ss", new Date());
    return format(fechaAux, "dd MMM yyyy", { locale: es });
  };

  const formatHora = (fecha) => {
    const fechaAux = parse(fecha, "yyyy-MM-dd HH:mm:ss", new Date());
    return format(fechaAux, "HH:mm", { locale: es });
  };

const redeemPrivatePlan = async () => {
  loadingStore.setLoading(true);
  try {
    let response = await fetchRedeemPrivatePlan(props.privatePlanPurchaseData.purchase.codigo);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchRedeemPrivatePlan(props.privatePlanPurchaseData.purchase.codigo);
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      emit('errorRedeeming', "Se ha producido un error canjeando el taller privado.");
    }

    const data = await response.json();

    if (data.errors) {
      loadingStore.setLoading(false);
      emit('errorRedeeming', data.message);
    } else {
      loadingStore.setLoading(false);
      emit('successRedeeming');
    }
  } catch (exception) {
    loadingStore.setLoading(false);
    emit('errorRedeeming', exception.message);
  } finally {
    visible.value = false;
    history.back();
  }
};

async function fetchRedeemPrivatePlan(codigo) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/redeems/redeem_private_plan", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "purchase_code": codigo
      }),
    });
}

  </script>
  
  <style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.modal-card {
  background-color: var(--white);
  padding: 1.75rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-xl);
  width: 90%;
  max-width: 540px;
  max-height: 88vh;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from { transform: translateY(12px) scale(0.98); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid var(--border-color-subtle);
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: var(--text-color-light);
  cursor: pointer;
  padding: 0 4px;
  transition: color var(--transition-fast);
}

.close-btn:hover {
  color: var(--text-color-dark);
}

.code-badge {
  font-weight: 700;
  font-size: 14px;
  background: var(--very-clear-gray);
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  color: var(--text-color-dark);
}

.details-box {
  background: var(--very-clear-gray);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-top: 1rem;
  margin-bottom: 1.25rem;
  font-size: 13.5px;
}

.detail-row {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.detail-item strong {
  color: var(--text-color-dark);
}

.user-card {
  background: #FFFFFF;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1rem 1.25rem;
  margin-top: 1rem;
}

.user-card-label {
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-color-light);
  display: block;
  margin-bottom: 0.65rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.circular-image {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--primary-light);
}

.user-text h3 {
  font-size: 1rem;
  margin-bottom: 2px;
  color: var(--text-color-dark);
}

.user-text p {
  font-size: 13px;
  margin-bottom: 0;
  color: var(--text-color-light);
}

.button-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-enabled {
  opacity: 1;
}
</style>
  